import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Access tokens live only 15 minutes. On any 401, silently refresh the session
// once and retry the original request; if refresh also fails, notify the app so
// it can return to the login screen instead of showing raw "Not authenticated".
let refreshPromise = null;
const SKIP_REFRESH_PATHS = ["/auth/login", "/auth/refresh"];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";
    const skip = SKIP_REFRESH_PATHS.some((p) => url.includes(p));
    if (error.response?.status === 401 && original && !original._retriedAfterRefresh && !skip) {
      original._retriedAfterRefresh = true;
      try {
        refreshPromise =
          refreshPromise ||
          api.post("/auth/refresh").finally(() => {
            refreshPromise = null;
          });
        await refreshPromise;
        return api(original);
      } catch {
        if (!url.includes("/auth/me")) {
          window.dispatchEvent(new CustomEvent("auth:expired"));
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Blob responses hide the JSON error body — read it out before formatting.
export async function formatBlobError(err) {
  try {
    if (err?.response?.data instanceof Blob) {
      const text = await err.response.data.text();
      return formatApiErrorDetail(JSON.parse(text).detail);
    }
  } catch {
    /* fall through to generic */
  }
  const generic = "Something went wrong. Please try again.";
  const detail = formatApiErrorDetail(err?.response?.data?.detail);
  return detail !== generic ? detail : err?.message || generic;
}

export default api;
